import { FORM_ERROR } from "final-form";
import { Form } from "react-final-form";

import { createOrganizationUser, NewUser } from "@/api/endpoints/organization";
import { postTrackingData } from "@/api/endpoints/track";
import { USER_ACCESS } from "@/constants";
import { useInviteContext } from "@/containers/InviteWizard/InviteContext";
import UserSettingFields, {
  USER_SETTINGS_FIELDS,
} from "@/containers/UserSettingFields";
import { EVENT } from "@/tracking_events";
import Button from "@components/Button";
import HelperText from "@components/Field/HelperText";
import Rhythm from "@components/Rhythm";
import Typography from "@components/Typography";
import { useWizard } from "@components/Wizard";

import styles from "../InviteWizard.module.css";

export type InviteProps = { className?: string };

const Invite: React.FunctionComponent<InviteProps> = () => {
  const { next, to } = useWizard();
  const { setUser, userDefaults, organizationUuid } = useInviteContext();

  const handleSave = async (user: NewUser) => {
    const response = await createOrganizationUser({
      organization_uuid: organizationUuid,
      user,
    });

    // Set the user received from the creation
    if (response.ok && response.data) {
      setUser(response.data);
      response.data.user_type === USER_ACCESS.READONLY ? next() : to(Infinity);

      response.data &&
        postTrackingData(EVENT.INVITE_USER, {
          user_type: response.data?.user_type,
        });
      return undefined;
    } else {
      if (response.data) {
        // Handle array of strings (form-level error)
        if (Array.isArray(response.data)) {
          return { [FORM_ERROR]: response.data.join(", ") };
        }

        // Handle object with field errors
        return Object.entries(response.data).reduce(
          (acc, [key, value]) => {
            if (Object.values(USER_SETTINGS_FIELDS).includes(key)) {
              acc[key] = (value as string[]).join(", ");
            }
            return acc;
          },
          {} as Record<string, string>
        );
      }
    }
  };

  return (
    <div>
      <Typography variant="h3" data-testid="user-add-step-1-title">
        {L.ORGANIZATION_INVITE_USER_INVITE_TITLE}
      </Typography>
      <Typography>{L.ORGANIZATION_INVITE_USER_SUBTITLE}</Typography>
      <Form onSubmit={handleSave} initialValues={userDefaults?.user || {}}>
        {({ handleSubmit, invalid, submitError, dirtySinceLastSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Rhythm className={styles.fields}>
              <UserSettingFields
                readOnlyFields={false}
                fields={[
                  USER_SETTINGS_FIELDS.EMAIL,
                  USER_SETTINGS_FIELDS.LANGUAGE,
                  USER_SETTINGS_FIELDS.ACCESS,
                ]}
              />
              {submitError && !dirtySinceLastSubmit && (
                <HelperText error content={submitError} />
              )}
              <div className={styles.actions}>
                <Button
                  type="submit"
                  disabled={invalid}
                  data-testid="user-add-step-1-submit"
                >
                  {L.ORGANIZATION_INVITE_SEND}
                </Button>
              </div>
            </Rhythm>
          </form>
        )}
      </Form>
    </div>
  );
};

export default Invite;
